/**
 * Every sentence the app says about how long a stored report result lasts (spec 152).
 *
 * Pure, no React. ONE module, because the wording drifted the moment it lived in two
 * places: the drawer's subline (`JobRunSummary`), the expired panel, the Queue page's
 * subtitle, the schedule editor's helper and the queue modal each carried their own,
 * and three of them were false on QA by 2026-10-01.
 *
 * ## The three clocks, and which ones a sentence may name
 *
 *   1. ad-hoc row TTL   — `expires_at`, one week (`RESULT_TTL_HOURS`), stamped on the row;
 *   2. schedule count   — "keep the last N runs" (`retain_count`), enforced by
 *                         `pruneScheduleResults` the moment a run finishes (spec 152 F2c);
 *   3. bucket age       — the R2 lifecycle rule deletes the result OBJECT by age whatever
 *                         1 or 2 say (QA: "Expire objects after 7 days", read from the
 *                         rule 2026-10-09; prod: 2 days by the bucket's comment, never
 *                         read). The API declares it (`resultMaxAgeDays`, from cb-api's
 *                         `RESULT_MAX_AGE_DAYS`) and folds it into `resultExpiresAt` on
 *                         every job.
 *
 * The rule every function here follows: **never promise longer than the bucket keeps
 * the object.** So:
 *
 *   - **A PERIOD ("kept for 7 days", "up to 2 days") is stated only when the API has
 *     declared the bucket's age.** Unknown — nothing read yet (`undefined`), or a payload
 *     that declares none (`null`: an older worker, an environment without the var) —
 *     states no period at all. Spec 152 review: these sentences used to fall back to the
 *     row's "1 week" when the age was unknown, which put a week-long promise on prod's
 *     screen (a 2-day bucket) for the length of every first read, and for good whenever
 *     the schedules read failed. "Unknown" is not "a week".
 *   - **A DATE is stated only where the run itself carries one** (spec 132, unchanged):
 *     `resultExpiry` — the API's `resultExpiresAt`, else the row's own `expiresAt`. That
 *     is also the instant `deriveState` turns the run Expired, so the sentence and the
 *     state can never disagree. Never a date synthesised from cadence × count.
 *   - "Not recorded" provenance gets no rule at all.
 */
import { RESULT_TTL_HOURS, resultExpiry } from '@config/queueConfig'
import { jobTrigger } from './jobIdentity'
import { fmtExpiresIn } from './queueFormat'

const DAY_HOURS = 24

/** A positive number of days, or null. The API sends a number; anything else is "unknown". */
export function maxAgeDaysOf(value) {
  if (value == null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : null
}

/** "1 day" / "7 days" / "12 hours" — a whole number of days says days, anything else hours. */
export function daysLabel(days) {
  const d = maxAgeDaysOf(days)
  if (d == null) return null
  if (Number.isInteger(d)) return `${d} day${d === 1 ? '' : 's'}`
  const hours = Math.max(1, Math.round(d * DAY_HOURS))
  return `${hours} hour${hours === 1 ? '' : 's'}`
}

/**
 * How long an AD-HOC result is kept: the row's one-week TTL, bounded by the bucket.
 * A bucket shorter than the week ("kept for 1 week" was five days too long against a
 * 2-day bucket) wins; a longer one does not extend the row.
 *
 * It is said in DAYS, the unit every other sentence beside it uses — "each for up to 7
 * days. Ad-hoc runs are kept for 1 week." named one duration two ways (spec 152 review).
 *
 * Null when the bucket's age is unknown (see the header): the row's week is only an upper
 * bound then, and the caller states no period at all.
 */
export function adHocRetentionLabel(maxAgeDays) {
  const d = maxAgeDaysOf(maxAgeDays)
  if (d == null) return null
  return daysLabel(Math.min(d, RESULT_TTL_HOURS / DAY_HOURS))
}

/** The schedule's count as a positive whole number, or null when it is not known. */
const countOf = (retainCount) => {
  const n = Number(retainCount)
  return retainCount != null && Number.isInteger(n) && n > 0 ? n : null
}

/** "last 3 runs" when the count is known, "most recent runs" when it is not. */
const runsPhrase = (retainCount) => {
  const n = countOf(retainCount)
  return n == null ? 'most recent runs' : `last ${n} runs`
}

const fmtExpiry = (ms) => new Date(ms).toLocaleString([], {
  month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
})

/**
 * The drawer subline's retention clause — the one line in the drawer that can state
 * something false about a user's own financial data. Lower-case: it follows
 * "returned in 4.8s · ".
 *
 *   expired                         → "the stored result is gone"
 *   from a schedule                 → "kept while it is one of this schedule’s last 3 runs,
 *                                      for up to 7 days" (count known), "…most recent
 *                                      runs…" (not known), no age when the API sent none
 *   …whose schedule is provably deleted → "kept until <its expiry>" (nothing prunes it,
 *                                      the bucket still ages it out), or spec 132's
 *                                      caution when no expiry is known
 *   with an expiry on the row       → "kept until <the EARLIER of the row's expiry and the
 *                                      bucket's>"
 *   known ad-hoc, no expiry         → "kept for 7 days" (the lesser of the week and the
 *                                      bucket, in days); null when the age is unknown
 *   provenance unknown, no expiry   → null: there is no true sentence to say
 *
 * Order matters: a schedule run is checked BEFORE the expiry, because its row carries
 * `expires_at = null` and the count is the rule the user chose.
 */
export function retentionClause({ job, expired = false, trigger }) {
  if (expired) return 'the stored result is gone'
  const maxAgeDays = maxAgeDaysOf(job?.resultMaxAgeDays)
  const expiry = resultExpiry(job)
  if (trigger?.fromSchedule) {
    if (job?.scheduleDeleted === true) {
      return expiry != null
        ? `its schedule was deleted · kept until ${fmtExpiry(expiry)}`
        : 'its schedule was deleted, so this result may no longer be available'
    }
    const age = daysLabel(maxAgeDays)
    const kept = countOf(job?.scheduleRetainCount) === 1
      ? 'kept while it is this schedule’s latest run'
      : `kept while it is one of this schedule’s ${runsPhrase(job?.scheduleRetainCount)}`
    return `${kept}${age ? `, for up to ${age}` : ''}`
  }
  if (expiry != null) return `kept until ${fmtExpiry(expiry)}`
  const adHoc = trigger?.key === 'manual' ? adHocRetentionLabel(maxAgeDays) : null
  return adHoc ? `kept for ${adHoc}` : null
}

/**
 * The expired panel's sentence. `gone` = the result route answered 410 for a run that
 * still read Ready — the object went before any date we knew about, so the sentence
 * says only that, and claims no rule.
 *
 * A schedule's run has TWO clocks and the sentence says which does what (spec 152
 * review). It used to end "Older runs keep their details, not their data." — but the
 * runs older than the last N are DELETED outright, row and object
 * (`pruneScheduleResults` → `deleteJobsByIds`), the moment the next run finishes. What
 * keeps its details without its data is a run still INSIDE the count whose object the
 * bucket has aged out — which is the run this panel is showing.
 */
export function expiredSentence({ job, trigger, gone = false }) {
  const kept = "The run details are kept — the data isn't."
  if (gone) return `This result is no longer stored. ${kept}`
  const maxAgeDays = maxAgeDaysOf(job?.resultMaxAgeDays)
  if (trigger?.fromSchedule && job?.scheduleDeleted !== true) {
    const age = daysLabel(maxAgeDays)
    const n = countOf(job?.scheduleRetainCount)
    if (n === 1) {
      return age
        ? `This schedule keeps its latest run. Its data can be opened for up to ${age}; its details stay until a newer run replaces it.`
        : 'This schedule keeps its latest run. Its data can stop being stored before its details do.'
    }
    const runs = runsPhrase(job?.scheduleRetainCount)
    if (!age) return `This schedule keeps its ${runs}. A run’s data can stop being stored before its details do.`
    const among = n == null ? 'one of them' : `one of the ${n}`
    return `This schedule keeps its ${runs}. Each run’s data can be opened for up to ${age}; its details stay while it is ${among}.`
  }
  const adHoc = trigger?.key === 'manual' ? adHocRetentionLabel(maxAgeDays) : null
  return adHoc ? `Results are kept for ${adHoc}. ${kept}` : kept
}

/**
 * The schedule editor's "Keep results" helper (decision 4), and the queue modal's.
 * "Scheduled results are kept by count, not by age" was false while the bucket deletes
 * by age; this says both bounds, and no age at all when the API did not send one.
 */
export function scheduleRetentionHelp({ retainCount, maxAgeDays }) {
  const age = daysLabel(maxAgeDays)
  if (countOf(retainCount) === 1) return `Keeps the latest run${age ? `, for up to ${age}` : ''}.`
  return `Keeps the ${runsPhrase(retainCount)}${age ? `, each for up to ${age}` : ''}.`
}

/**
 * The Queue page subtitle's clause, after "N running · N ready to view · ". It covers
 * BOTH populations, so with a known bucket age it says "up to" that age: an ad-hoc run
 * keeps the lesser of its TTL and the bucket's age, a schedule's run at most the bucket's
 * age — so the bucket's age is the one bound true of every row. Deliberately NOT capped at
 * the ad-hoc week: with a bucket longer than that, a cap would understate how long a
 * schedule's runs last, which is false too.
 *
 * Null when the age is unknown — not read yet, or not declared: the page then states no
 * retention at all. It used to say "results are kept 1 week" then, on every first paint
 * (the age was read off rows that had not arrived), five days longer than prod's bucket.
 */
export function queueRetentionSummary(maxAgeDays) {
  const age = daysLabel(maxAgeDays)
  return age ? `results are kept for up to ${age}` : null
}

/**
 * The Manage Schedules page's age sentence, appended to its "Results, not just runs"
 * panel (spec 152 review). That panel said a schedule "keeps its most recent few rather
 * than expiring them on a clock" — false while the bucket deletes every result by age.
 * Null when no age is declared or read: the panel then states the count rule alone.
 */
export function schedulesPageRetention(maxAgeDays) {
  const age = daysLabel(maxAgeDays)
  return age ? `Each run’s result can be opened for up to ${age} after it runs.` : null
}

/**
 * May something other than its expiry remove this run's result sooner? A live schedule's
 * run can: `pruneScheduleResults` deletes it as soon as N newer runs have finished, which
 * can be long before its `resultExpiresAt`. Only two runs are PROVABLY free of that — a
 * known ad-hoc run and one whose schedule is provably deleted — so only they get the
 * exact countdown. A run whose provenance is "Not recorded" might be a schedule's, and is
 * treated as one: the bound is true either way, the countdown would not be.
 */
const exactExpiry = (job) => jobTrigger(job).key === 'manual' || job?.scheduleDeleted === true

/**
 * The short note beside a Ready row's badge — the queue table's status cell and the
 * bell's line. Null when the run carries no expiry.
 *
 *   ad-hoc / deleted schedule  → "expires in 3d" — the instant `deriveState` expires it on
 *   live schedule's run, or a  → "up to 3d left" — an upper bound, never a countdown
 *   run of unrecorded provenance
 *
 * Spec 152 review: a bare "expires in 3d" on a schedule's run promised days the run does
 * not get. Measured on QA D1 (2026-10-09): schedule a9a6bbb9 (daily, keep 5) held a run
 * 5.35 days old that would read "expires in 1d" and is pruned when the next run finishes,
 * about 16 hours later. Its countdown is a bound, and the note says so; the drawer's
 * subline names the count beside it. Never a date synthesised from cadence × count.
 */
export function expiryNote(job, now = Date.now()) {
  const expiry = resultExpiry(job)
  if (expiry == null) return null
  const when = fmtExpiresIn(expiry, now)
  // The ticking clock can pass the instant before the row is re-derived as Expired.
  if (when === 'expired') return 'expired'
  if (!exactExpiry(job)) return `up to ${when.replace(/^in /, '')} left`
  return `expires ${when}`
}

/**
 * The plain sentence for a result the server says is gone (410), wherever a reader would
 * otherwise get a toast or "Request failed with status code 410".
 */
export const RESULT_GONE_SENTENCE = 'This saved result is no longer stored, so it can’t be opened or downloaded. Run the report again for current figures.'

/**
 * The plain sentence for a run the server no longer has at all (404 "Job not found").
 * Retention is one cause and, since spec 152 F2c, the quickest one: a schedule's run is
 * deleted the moment N newer runs finish, so the "Open the report" link in a scheduled
 * email can outlive its run within days. The owner-scoped read cannot say WHICH cause, so
 * the sentence names each without choosing.
 */
export const RUN_UNAVAILABLE_SENTENCE = 'This run is no longer available. It may have been dismissed, it may be older than what its schedule or the queue keeps, or it may belong to someone else. Run the report again for current figures.'

/**
 * Spec 168 — the plain sentence for a saved run opened on a report page that is not its
 * own. Measured on QA 2026-10-12: `/reports/363?jobId=<an Aged A/R run>` drew that run's
 * 69 rows on the General Ledger page as its "Saved result", and cb-api served a report-49
 * run, byte for byte, as `/reports/190/data`. Every reopen now refuses such a run, and
 * this is what it says instead of a raw "Request failed with status code …".
 */
export const OTHER_REPORT_RUN_SENTENCE = 'This saved result is from a different report, so it can’t be shown here. Run this report for current figures.'

/** Did this request fail because the stored result is gone? (cb-api answers 410.) */
export function isResultGone(err) {
  return err?.response?.status === 410
}

/**
 * cb-api's 404 for a run id it has no row for — owner-scoped `getJob` returned nothing
 * (`cb-api/src/domains/reports/jobs/routes.js`, `{ error: 'Job not found' }` on the
 * detail and result routes). NOT every 404: the same router answers 404 "Report jobs not
 * enabled" when the feature is off, and that must not be described as a removed run.
 */
export function isRunNotFound(err) {
  return err?.response?.status === 404 && err?.response?.data?.error === 'Job not found'
}

/**
 * The plain sentence for a saved-run read that failed for a retention reason — or, since
 * spec 168, because the run belongs to another report — or null.
 * `reportJobService.status` and `.result` put it on the error itself, so the report
 * page's reopen and all 21 specialized pages' reopens — which show `err.message` —
 * stop saying "Request failed with status code 410 / 404".
 */
export function savedRunErrorSentence(err) {
  if (isResultGone(err)) return RESULT_GONE_SENTENCE
  if (isRunNotFound(err)) return RUN_UNAVAILABLE_SENTENCE
  if (isOtherReportRun(err)) return OTHER_REPORT_RUN_SENTENCE
  return null
}

/** cb-api's `code` on its 409 for a named run of another report (`/reports/:id/data?jobId=`). */
export const REPORT_MISMATCH_CODE = 'report-mismatch'

/**
 * Spec 168 — is `job` (a normalized job: `reportId` is a string) a run of the report page
 * `reportId` (ReportView parses it to a number)? Compared as strings. A job that names no
 * report is NOT this report's: nothing could show it belongs here, and every link that
 * opens a run is built from the run's own `reportId` (0 of 797 QA rows lack one).
 */
export function isRunOfReport(job, reportId) {
  const own = job?.reportId
  if (own == null || own === '' || reportId == null || reportId === '') return false
  return String(own) === String(reportId)
}

/** The refusal a client guard throws for a run of another report; `err.message` is the sentence. */
export function otherReportRunError() {
  return Object.assign(new Error(OTHER_REPORT_RUN_SENTENCE), { reportMismatch: true })
}

/**
 * Was this read refused because the run belongs to another report? Either a client guard's
 * own refusal (`otherReportRunError`) or cb-api's 409 with `code: 'report-mismatch'`. The
 * code is checked, not the status alone: the jobs router answers 409 "Not ready" too.
 */
export function isOtherReportRun(err) {
  if (err?.reportMismatch === true) return true
  return err?.response?.status === 409 && err?.response?.data?.code === REPORT_MISMATCH_CODE
}
